<script setup lang="ts">
/** Hintergrund der Fläche: Farbe oder Muster/Grafik aus der Asset-Bibliothek (Typ „Hintergrund“). */
import { computed, onMounted, ref } from 'vue'
import { useDesignStore } from '../../../store/useDesignStore'
import { listAssets, type DesignAssetSummary } from '../../../services/designApi'
import { getWidgetConfig } from '../../../runtime/widgetConfig'

const props = defineProps<{ palette: string[]; allowFreeColor: boolean; productId?: number | null }>()
const store = useDesignStore()
const cfg = getWidgetConfig() as any

const bg = computed(() => store.activeDoc?.canvas.background)
const current = computed(() => (bg.value?.type === 'color' ? bg.value.value : '#FFFFFF'))
const currentAssetId = computed(() => (bg.value?.type === 'asset' ? bg.value.assetId : null))
const swatches = computed(() => [...new Set(['#FFFFFF', '#F8FAFC', '#F4F1EA', '#0F172A', ...props.palette])])

const patterns = ref<DesignAssetSummary[]>([])
const categories = ref<Array<{ id: number; name: string }>>([])
const activeCategory = ref<number | null>(null)
const loading = ref(true)
onMounted(async () => {
  try {
    const res = await listAssets({ editorBaseUrl: cfg.editorBaseUrl, productId: props.productId ?? null, type: 'background' })
    patterns.value = res.items || []
    // Nur Kategorien mit Hintergründen anbieten (Muster, Feste & Jahreszeiten, Texturen …)
    const used = new Set(patterns.value.map((a) => a.categoryId))
    categories.value = (res.categories || []).filter((c) => c.type === 'background' && used.has(c.id)).map((c) => ({ id: c.id, name: c.name }))
  } catch {
    patterns.value = []
  } finally {
    loading.value = false
  }
})
const visiblePatterns = computed(() => (activeCategory.value == null ? patterns.value : patterns.value.filter((a) => a.categoryId === activeCategory.value)))
const assetUrl = (a: DesignAssetSummary) => a.svgUrl || a.pngUrl || a.url || ''
function setPattern(a: DesignAssetSummary) {
  const url = assetUrl(a)
  if (url) store.setBackground({ type: 'asset', assetId: a.id, url })
}
</script>

<template>
  <div class="bgp">
    <section class="bgp__sec">
      <h3 class="bgp__title">Farbe</h3>
      <p class="bgp__hint">Füllt die gesamte Fläche bis in den Beschnitt.</p>
      <div class="bgp__swatches">
        <button v-for="c in swatches" :key="c" type="button" class="bgp__swatch" :class="{ 'bgp__swatch--on': !currentAssetId && c.toLowerCase() === current.toLowerCase() }" :style="{ background: c }" :aria-label="c" @click="store.setBackground({ type: 'color', value: c })" />
        <input v-if="allowFreeColor" type="color" class="bgp__pick" :value="current" aria-label="Eigene Farbe" @input="store.setBackground({ type: 'color', value: ($event.target as HTMLInputElement).value })" />
      </div>
    </section>

    <section v-if="loading || patterns.length" class="bgp__sec">
      <h3 class="bgp__title">Muster &amp; Hintergründe</h3>
      <div v-if="loading" class="bgp__hint">Wird geladen…</div>
      <div v-if="!loading && categories.length > 1" class="bgp__chips" role="tablist" aria-label="Hintergrund-Kategorien">
        <button type="button" class="bgp__chip" :class="{ 'bgp__chip--on': activeCategory == null }" @click="activeCategory = null">Alle</button>
        <button v-for="c in categories" :key="c.id" type="button" class="bgp__chip" :class="{ 'bgp__chip--on': activeCategory === c.id }" @click="activeCategory = activeCategory === c.id ? null : c.id">{{ c.name }}</button>
      </div>
      <ul v-if="!loading" class="bgp__grid">
        <li v-for="a in visiblePatterns" :key="a.id">
          <button type="button" class="bgp__pattern" :class="{ 'bgp__pattern--on': currentAssetId === a.id }" :title="a.name" :aria-label="`Muster ${a.name}`" @click="setPattern(a)">
            <img :src="a.previewUrl || assetUrl(a)" :alt="a.name" loading="lazy" draggable="false" />
            <span>{{ a.name }}</span>
          </button>
        </li>
      </ul>
    </section>
  </div>
</template>

<style scoped>
.bgp { display: grid; gap: 18px; padding: 14px 16px; }
.bgp__sec { display: grid; gap: 8px; }
.bgp__title { margin: 0; font-size: 13px; font-weight: 700; color: #0f172a; }
.bgp__hint { margin: 0; font-size: 12px; color: rgba(15, 23, 42, 0.6); }
.bgp__swatches { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.bgp__swatch { width: 32px; height: 32px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.2); cursor: pointer; padding: 0; }
.bgp__swatch--on { box-shadow: 0 0 0 2px var(--pfe-color-accent, #e30b17); }
.bgp__pick { width: 32px; height: 32px; padding: 0; border: 1px solid rgba(15, 23, 42, 0.2); border-radius: 50%; background: none; cursor: pointer; }
.bgp__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.bgp__chip { padding: 4px 10px; border: 1px solid rgba(15, 23, 42, 0.15); border-radius: 999px; background: #fff; font: inherit; font-size: 11.5px; color: rgba(15, 23, 42, 0.75); cursor: pointer; }
.bgp__chip--on { border-color: var(--pfe-color-accent, #e30b17); color: var(--pfe-color-accent, #e30b17); background: color-mix(in srgb, var(--pfe-color-accent, #e30b17) 8%, #fff); }
.bgp__grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.bgp__pattern { display: grid; gap: 4px; width: 100%; padding: 6px; border: 1px solid rgba(15, 23, 42, 0.12); border-radius: 8px; background: #fff; cursor: pointer; font: inherit; font-size: 11px; color: rgba(15, 23, 42, 0.7); text-align: center; }
.bgp__pattern img { width: 100%; aspect-ratio: 3 / 2; object-fit: cover; border-radius: 4px; }
.bgp__pattern--on { border-color: var(--pfe-color-accent, #e30b17); box-shadow: 0 0 0 1px var(--pfe-color-accent, #e30b17); }
</style>
